import { getCookie } from '@api/HelperAPI';
import { datadogLogs } from '@datadog/browser-logs';
import {
    GetFormalOffersResponse,
    fetchGetApplication,
    fetchGetFormalOffers,
    fetchGetFundingRequirementsIwocapay,
    fetchGetIwocapayBuyerSpendingLimitRequest,
    fetchGetProductOffers,
    fetchGetStateByStateKey,
    GetContextResponse,
    fetchPutState,
    PutStateRequestBody,
    fetchGetSplitTest,
    fetchGetUserConsent,
    fetchGetDrawDownRepaymentScheduleCreditFacility
} from '@iwoca/lapi-client/edge';
import {
    GetBuyerSpendingLimitResponse,
    fetchGetBuyerSpendingLimit
} from '@iwoca/lapi-client/iwocapay';
import { useMutation, useQuery } from '@tanstack/react-query';

import { TGetContext } from './lapi.types';
import { getAccountsQueryConfig } from './queryConfigs';
import { SITE_CONTEXT_CACHE_KEY } from '../../iwQueryClient/iwQueryClient';
import { useStateKey } from '../../iwQueryClient/useStateKey';
import { mapOfferDurationToMonths } from '../../myAccount/iwocaPay/dashboard/mapOfferDurationToMonths';
import { TSplitTestBranch } from '../../myAccount/multiLoanDashboard/AccountFeatureEvaluator/AccountFeatureEvaluator';

interface QueryOptions {
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
    refetchInterval?: false | number | ((data: any) => number | false);
    enabled?: boolean;
}

/**
 * Allows to ensure some values of a typed object are not nullable
 *
 * type Item = {
 *   cost: number | null
 *   name: string
 *   date: string | null
 *   rating: number | null
 * }
 *
 * type FullItem = Ensure<Item, 'rating' | 'date'>
 *
 */
type Ensure<T, K extends keyof T> = T & RequiredNotNull<Pick<T, K>>;

type RequiredNotNull<T> = {
    [P in keyof T]-?: NonNullable<T[P]>;
};

type FormalOffers = NonNullable<
    GetFormalOffersResponse['data']
>['formal_offers'];

export type SanitisedFormalOffers = Ensure<
    FormalOffers[number],
    'max_amount' | 'available_cash' | 'timeout'
>[];

const sortFormalOffersByDuration = (
    offers:
        | NonNullable<GetFormalOffersResponse['data']>['formal_offers']
        | undefined
) => {
    if (!offers) return [];

    return offers.sort((a, b) => {
        const aMonths = mapOfferDurationToMonths(a);
        const bMonths = mapOfferDurationToMonths(b);

        return aMonths - bMonths;
    });
};

export function useGetFormalOffers({
    enabled = true
}: { enabled?: boolean } = {}) {
    const stateKey = useStateKey();

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ['fetchGetFormalOffers', stateKey],
        queryFn: () => fetchGetFormalOffers({ stateKey }),
        enabled
    });

    return {
        formalOffers:
            sortFormalOffersByDuration(data?.data?.formal_offers) || null,
        loadingFormalOffers: isLoading && enabled,
        formalOffersError: isError,
        fetchFormalOffers: refetch
    };
}

export function useGetFirstIwocapayOffer({
    enabled = true
}: { enabled?: boolean } = {}) {
    const { formalOffers, loadingFormalOffers, fetchFormalOffers } =
        useGetFormalOffers({ enabled });

    const firstIwocapayOffer = formalOffers?.find(
        (offer) => offer.product_type === 'iwocapay'
    );

    return {
        firstIwocapayOffer: firstIwocapayOffer,
        isLoading: loadingFormalOffers,
        fetchFirstIwocapayOffer: fetchFormalOffers
    };
}

export function useGetFormalOffer(offerId: string | undefined) {
    const stateKey = useStateKey();

    const enabled = Boolean(offerId);

    const { data, isLoading, isError } = useQuery({
        queryKey: ['fetchGetFormalOffers', stateKey],
        queryFn: () => fetchGetFormalOffers({ stateKey }),
        enabled: enabled,
        select: (data) =>
            data.data?.formal_offers.find(
                (formal_offer) => formal_offer.offer_id === offerId
            )
    });

    return {
        formalOffer: data || null,
        loadingFormalOffer: isLoading && Boolean(enabled),
        formalOfferError: isError
    };
}

type ProductType = NonNullable<
    GetFormalOffersResponse['data']
>['formal_offers'][0]['product_type'][];

export const useApprovedNotTakenOffers = ({
    excludedProducts = []
}: {
    excludedProducts?: ProductType;
} = {}): {
    approvedNotTakenOffers: SanitisedFormalOffers;
    loadingApprovedNotTaken: boolean;
} => {
    const { formalOffers, loadingFormalOffers } = useGetFormalOffers();

    const sanitisedOffers = formalOffers?.filter((offer) => {
        if (!offer.max_amount) return false;
        if (!offer.available_cash) return false;
        if (!offer.timeout) return false;
        return true;
    });

    const validOffers = sanitisedOffers?.filter(
        (offer) => offer.is_valid === true
    );

    const filteredOffers = validOffers?.filter(
        (offer) => !excludedProducts.includes(offer.product_type)
    );

    const notTakenOffers = filteredOffers?.filter((offer) => {
        return Math.abs(offer.max_amount! - offer.available_cash!) < 1;
    });

    return {
        approvedNotTakenOffers: notTakenOffers as SanitisedFormalOffers,
        loadingApprovedNotTaken: loadingFormalOffers
    };
};

export function useGetSpendingLimitRequest() {
    const stateKey = useStateKey();
    const { data, isLoading, error } = useQuery({
        queryKey: ['fetchGetIwocapayBuyerSpendingLimitRequest', stateKey],
        queryFn: () => fetchGetIwocapayBuyerSpendingLimitRequest({ stateKey })
    });

    return {
        spendingLimitRequest: data?.data,
        loadingSpendingLimitRequest: isLoading,
        spendingLimitRequestError: error
    };
}

export function useGetFundingRequirementsIwocaPay() {
    const stateKey = useStateKey();
    const { firstIwocapayOffer } = useGetFirstIwocapayOffer();

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['fetchGetFundingRequirements', stateKey, firstIwocapayOffer],
        queryFn: () =>
            fetchGetFundingRequirementsIwocapay({
                stateKey,
                offerId: firstIwocapayOffer?.offer_id!
            }),
        enabled: Boolean(firstIwocapayOffer)
    });

    return {
        fundingRequirement: data?.data || null,
        loadingFundingRequirement: isLoading,
        fetchFundingRequirements: refetch
    };
}

// Please keep this list up to date with currently used split tests in Pandora.
// Add any new split tests as required.
export type TSplitTestNames =
    | 'uni_approved_view_test_2023'
    | 'business_details_reduction'
    | 'no_pg_test_2025'
    | 'non_applicant_pg_consent'
    | 'document_upload_feedback_test_q3_2025'
    | 'credit_compass_test'
    | 'self_serve_account_pages'
    | 'de_repayment_reduction_test_2025';

type splitTests = {
    data: {
        user_split_tests: { [k: string]: boolean }[];
    };
};

export function useSplitTests(splitTestName: TSplitTestNames): boolean {
    const { data: splitTests } = useQuery({
        queryKey: [SITE_CONTEXT_CACHE_KEY],
        select: (data: splitTests) => data.data.user_split_tests
    });

    const splitTestsAsObj = splitTests!.reduce((acc, obj) => {
        const [key, val] = Object.entries(obj)[0];

        return { ...acc, [key]: val };
    }, {});

    return splitTestsAsObj[splitTestName];
}

export function useGetProductOffers(stateKey: string) {
    const { data, isLoading, isError, refetch, isFetching } = useQuery({
        queryKey: ['product_offers', stateKey],
        queryFn: () => fetchGetProductOffers({ stateKey }),
        select: (data) => data.data
    });

    return { data, isLoading, isError, refetch, isFetching };
}

export function useGetApplication(stateKey: string) {
    const { data, isLoading, isError } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () => fetchGetApplication({ stateKey }),
        select: (data) => data.data
    });

    return { data, isLoading, isError };
}

export function useGetCustomerState(stateKey: string) {
    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ['customer_state', stateKey],
        queryFn: () => fetchGetStateByStateKey({ stateKey }),
        select: (data) => data.data
    });

    return { data, isLoading, isError, refetch };
}

export const useGetDocuments = (stateKey: string) => {
    const { data, isLoading, isError } = useQuery({
        queryKey: ['documents', stateKey],
        queryFn: async () => {
            // eslint-disable-next-line iwoca/prefer-lapi-client
            const response = await fetch(
                `/api/lending/edge/document_upload/${stateKey}/`
            );
            const data = response.json();
            return data;
        }
    });
    return { data, isLoading, isError };
};

export function useGetAccounts(queryOptions?: QueryOptions) {
    const stateKey = useStateKey();

    return useQuery({
        ...getAccountsQueryConfig(stateKey),
        enabled: queryOptions?.enabled ?? true,
        select: (data) => data.data!
    });
}

export function useGetContext() {
    const { data, isLoading, isError, refetch, isFetching } = useQuery({
        queryKey: [SITE_CONTEXT_CACHE_KEY],
        select: (data: GetContextResponse) => data.data
    });

    return {
        data: data as TGetContext['data'],
        isLoading,
        isError,
        refetch,
        isFetching
    };
}

export type TSpendingLimit =
    GetBuyerSpendingLimitResponse['data']['active_spending_limit'];
export function useGetBuyerSpendingLimit() {
    const stateKey = useStateKey();

    const { data, isLoading } = useQuery({
        queryKey: ['fetchGetBuyerSpendingLimit', stateKey],
        queryFn: () => fetchGetBuyerSpendingLimit({ stateKey })
    });

    return {
        buyerSpendingLimit: data?.data,
        loadingBuyerSpendingLimit: isLoading
    };
}

export function usePutStateMutation(stateKey: string) {
    const { mutateAsync, isError, isLoading } = useMutation({
        mutationFn: async ({ body }: { body: PutStateRequestBody }) => {
            await fetchPutState({
                stateKey,
                body
            });
        },
        onError: (error, variables) => {
            if (error instanceof Error) {
                datadogLogs.logger.error(
                    `Fetch PUT /state/${stateKey}/ error`,
                    {
                        requestBody: variables,
                        error: error.message
                    }
                );
            }
        }
    });

    return {
        mutatePutState: mutateAsync,
        isLoading,
        isError
    };
}

export function useSplitTestBranch(args: {
    test: string;
    suspense: false;
    cookieOnly?: boolean;
    enabled?: boolean;
}): string | undefined;
export function useSplitTestBranch(args: {
    test: string;
    suspense?: true;
    cookieOnly?: boolean;
    enabled?: boolean;
}): string;
export function useSplitTestBranch({
    test,
    suspense = true,
    cookieOnly = false,
    enabled = true
}: {
    test: string;
    suspense?: boolean;
    cookieOnly?: boolean;
    enabled?: boolean;
}) {
    const stateKey = useStateKey();

    // Check cookie
    const cookieBranch = getCookie(`split_test_${test}`);

    const shouldFetch = !cookieOnly && !cookieBranch && enabled;

    const { data: branchFromApi } = useQuery({
        queryKey: ['fetchGetSplitTest', stateKey, test],
        queryFn: () =>
            fetchGetSplitTest({
                stateKey,
                experimentName: test
            }).catch(() => ({ data: { branch: 'EXCLUDE' } })),
        select: ({ data }) => data.branch,
        enabled: shouldFetch,
        suspense
    });

    return cookieBranch || (branchFromApi as TSplitTestBranch);
}

export const useGetUserConsent = () => {
    const stateKey = useStateKey();

    const { data } = useQuery({
        queryKey: ['fetchGetUserConsent', stateKey],
        queryFn: () =>
            fetchGetUserConsent({
                stateKey: stateKey as string,
                provider: 'truelayer_open_banking'
            }),
        select: (data) => data.data
    });

    return {
        userConsent: data?.consent_active ?? false
    };
};

export const useDrawdownRepaymentSchedule = ({
    offerId,
    drawdownAmount,
    enabled = true
}: {
    offerId: string;
    drawdownAmount: number;
    enabled?: boolean;
}) => {
    const stateKey = useStateKey();

    return useQuery({
        queryKey: [
            'drawdownRepaymentSchedule',
            offerId,
            stateKey,
            drawdownAmount
        ],
        queryFn: () =>
            fetchGetDrawDownRepaymentScheduleCreditFacility({
                stateKey,
                offerId,
                query: { amount: drawdownAmount }
            }),
        enabled: Boolean(offerId) && enabled,
        select: (data) => data.data
    });
};
